import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import { BaseView } from '../../base-view';
import { Utils } from '../../../common/utils';
import { Filtering } from '../../../service/filtering/filtering';
import { RequestHandler } from '../../../service/OffService/request-handler';
import { DashboardService } from '../../../service/events/dashboard.service';
import { StockApiService } from '../../../service/api/stock-api.service';

import { ColInterface } from 'src/app/common/components/common-list-table/col.interface';
import { ColStylesInterface } from 'src/app/common/components/common-list-table/col-styles.interface';
import { FormType } from '@fitogest/reactive-form-builder/interfaces';

import { StockageModel } from 'src/app/models/stock/stokage.model';

import { EntradasEnvironmentDefinition } from './environment-variables';
import { EntradasFormDefinition } from './entradas-form/form-control/form-definition';
import { createCsv } from '@fitogest/reactive-form-builder/csv';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-entradas',
    standalone: false,
    templateUrl: './entradas.component.html',
    styleUrls: ['./entradas.component.scss'],
    providers: [MessageService]
})
export class EntradasComponent extends BaseView<StockageModel> implements OnInit {

    public form: EntradasFormDefinition | null = null;

    public env: EntradasEnvironmentDefinition = new EntradasEnvironmentDefinition();

    public filter: Filtering<Record<string, string>> = new Filtering<Record<string, string>>();

    public getRequest: RequestHandler<StockageModel[]> = this.stockApi.entradas.GET;

    public deleteRequest: RequestHandler<StockageModel> = this.stockApi.entradas.DELETE;

    public override model: StockageModel = {} as StockageModel;

    public override canShow: Record<string, boolean> = {};

    public readonly menuItemId = 'stock-entradas';

    public readonly btnAnadir = 'Añadir Entrada';

    public readonly showStockReto: boolean = Boolean(environment.features.showStockReto);

    public tipoPlantaSelectButton = 0;

    public optionsTipoPlanta = [
        { label: 'Todas', value: 0 },
        { label: 'Frutal', value: 'frutal' },
        { label: 'Ornamental', value: 'ornamental' },
        { label: 'Forestal', value: 'forestal' }
    ];

    public cols: ColInterface[] = [
        {
            field: 'finca_nombre',
            header: this.env.fincaName,
            visible: this.env.appName !== 'viverosblanco' && this.env.appName !== 'covidai' && this.env.appName !== 'detorres'
        },
        { field: 'proveedor', header: 'Proveedor', visible: this.env.appName === 'covidai' },
        { field: 'cliente', header: 'Cliente', visible: this.env.appName === 'covidai' },
        { field: 'ubicacion', header: 'Ubicación', width: '75px', visible: this.env.appName === 'viverosblanco' },
        { field: 'ubicacion_destino', header: 'Ubicación destino', width: '75px', visible: this.env.appName === 'viverosblanco' },
        { field: 'parcela', header: 'Ubicación', width: '75px', visible: this.env.appName === 'covidai' },
        { field: 'parcela_destino', header: 'Ubicación destino', width: '75px', visible: this.env.appName === 'covidai' },
        { field: 'producto_nombre', header: 'Producto', visible: !this.env.showStockTipoDeDato },
        {
            field: 'producto_nombre',
            header: this.env.appName === 'viverosblanco' ? 'Producto' : 'Producto o semilla',
            visible: this.env.showStockTipoDeDato
        },
        {
            field: 'marca_comercial',
            header: 'Marca\xa0comercial',
            visible: this.env.showStockMarcaComercial && this.env.appName !== 'bravosol'
        },
        { field: 'fecha', header: 'Fecha', width: '90px' },
        { field: 'fecha_injerto', header: 'Fecha\xa0injerto', width: '90px', visible: this.env.appName === 'viverosblanco' },
        { field: 'albaran', header: 'Albarán/Factura' },
        { field: 'tipo', header: 'Tipo', width: '90px' },
        { field: 'unidades', header: 'Unidades', footer: '' },
        {
            field: 'tipo_dato',
            header: 'Tipo de producto',
            visible: this.env.showStockTipoDeDato && this.env.appName !== 'viverosblanco' && this.env.appName !== 'covidai'
        },
        {
            field: 'variedad',
            header: 'Variedad',
            width: '220px',
            visible: this.env.appName === 'viverosblanco' || this.env.appName === 'covidai'
        },
    ];

    public constructor(
        public stockApi: StockApiService,
        private readonly dashboardEvents: DashboardService,
        private readonly router: Router,
        private readonly messageService: MessageService
    ) {
        super(dashboardEvents, stockApi.entradas.GET, stockApi.entradas.DELETE);
    }

    public ngOnInit(): void {
        this.dashboardEvents.setSubmenuItem('stock', this.menuItemId);

        timer(0, 50).pipe(
            filter(() => !this.getRequest.isLoading),
            take(1)
        ).subscribe(() => {
            this.updateFooter(this.filter, this.cols);
        });

        this.getRequest.response((data: StockageModel[] | undefined) => {
            this.normalizeTipoDatoRows(data);
            this.updateFooter(this.filter, this.cols);
        });

        this.filter.addFilter((data: any) =>
            data.filter((row: any) => {
                if (this.tipoPlantaSelectButton === 0) return true;
                return row['tipo_planta'] === this.tipoPlantaSelectButton;
            })
        );
    }

    public add(): void {
        this.router.navigate(['dashboard', 'stock-crear-entrada']);
    }

    public edit(data: any): void {
        this.router.navigate(['dashboard', 'stock-editar-entrada', data.id]);
    }

    public duplicate(data: any): void {
        this.router.navigate(['dashboard', 'stock-duplicar-entrada', data.id]);
    }

    public override show(modal: string, data: any): void {
        const modalModel = this.buildModalModel(data);

        this.model = modalModel;
        super.show(modal, modalModel);

        const formType = modal === 'delete'
            ? +FormType.DELETION
            : +FormType.INFORMATIVE;

        this.form = new EntradasFormDefinition(
            formType,
            modalModel,
            null
        );

        this.form.model = {
            ...this.form.model,
            ...modalModel
        };

        this.form.refreshLineFieldLabels();
    }

    public globalConditionalStyle(value: string, col: ColInterface): ColStylesInterface {
        if (col.field === 'unidades' && (+value) < 0) {
            return {
                textAlign: 'center',
                color: 'red'
            };
        }

        return { textAlign: 'center' };
    }

    public globalVisualTransform(value: string, col: ColInterface): string {
        if (col.field === 'unidades') {
            const num = Number(value);
            if (Number.isInteger(num)) {
                return num >= 0
                    ? String(num)
                    : '-' + String(Math.abs(num));
            }
            return num >= 0
                ? Utils.decimalFormat(value, 3)
                : '-' + Utils.decimalFormat(Math.abs(num), 3);
        }

        if (col.field === 'tipo') {
            switch (value) {
                case 'E':
                case 'Entrada':
                    return 'Entrada';
                case 'S':
                case 'Salida':
                    return 'Salida';
                case 'P':
                case 'Producción':
                    return 'Producción';
                case 'MOV':
                case 'Movimiento':
                    return 'Movimiento';
                case 'MER':
                case 'Merma':
                    return 'Merma';
                default:
                    return 'Desconocido';
            }
        }

        return value;
    }

    public override updateFooter(filter: Filtering<Record<string, string>>, cols: ColInterface[]): void {
        super.updateFooter(filter, cols);

        const unidadesCol = cols.find(col => col.field === 'unidades');

        if (unidadesCol && unidadesCol.footer != null) {
            const num = parseFloat(String(unidadesCol.footer).replace(',', '.'));

            if (!isNaN(num)) {
                unidadesCol.footer = Number.isInteger(num)
                    ? String(num)
                    : Utils.decimalFormat(Math.abs(num), 3);
            }
        }
    }

    public downloadCsv(): void {
        this.form = new EntradasFormDefinition(
            +FormType.EXPORT,
            this.filtering.value as unknown as StockageModel,
            null
        );

        const csvContent = createCsv(
            this.form.fieldNames,
            this.form.fieldLabels,
            this.filtering.value as StockageModel[]
        );

        const csvLink = document.getElementById('csvLink') as HTMLAnchorElement;

        csvLink.download = this.form.formFields.name + '.csv';
        csvLink.href = encodeURI(csvContent);
        csvLink.click();
    }

    public filtrarPorTipoPlanta(): void {
        this.filter.filter();
        this.updateFooter(this.filter, this.cols);
    }

    public downloadDeclaracion(): void {
        if (this.tipoPlantaSelectButton === 0 || this.tipoPlantaSelectButton === null) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Tipo de planta requerido',
                detail: 'Debe seleccionar un tipo de planta específico para generar la declaración.',
                life: 4000
            });
            return;
        }

        const excludedTipos = ['Salida', 'Merma'];

        const filteredData = ((this.filter.value as any[]) ?? []).filter((row: any) =>
            !excludedTipos.includes(row['tipo'])
        );

        if (filteredData.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Sin datos',
                detail: 'No hay entradas del tipo de planta seleccionado.',
                life: 4000
            });
            return;
        }

        const currentYear = new Date().getFullYear();
        const tipoLabel = (this.optionsTipoPlanta.find(o => o.value === this.tipoPlantaSelectButton)?.label ?? '').toUpperCase();
        const empresaName = this.env.empresaName ?? '';

        const headers = [
            'ESPECIE',
            'FORMATO',
            'UDS/BANDEJA',
            'UDS DE BANDEJAS',
            'UDS PRODUCIDAS',
            `LOTE ${currentYear}`,
            'Cód. MB O CPM',
            'Categoría MFR'
        ];

        const safeInt = (val: any): string => {
            const n = parseFloat(String(val ?? ''));
            return isNaN(n) ? '' : String(Math.round(n));
        };

        const rows = filteredData.map((row: any) => [
            row['producto_nombre'] ?? '',
            row['formato'] ?? '',
            safeInt(row['uds_bandeja']),
            safeInt(row['unidades']),
            safeInt(row['uds_producidas']),
            row['lote'] ?? '',
            row['cod_mb'] ?? '',
            row['categoria_mfr'] === '1' || row['categoria_mfr'] === 1 ? 'Identificado' : ''
        ]);

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(
            `DECLARACIÓN DE ${tipoLabel}ES ${currentYear} — ${empresaName}`,
            doc.internal.pageSize.getWidth() / 2,
            14,
            { align: 'center' }
        );

        autoTable(doc, {
            head: [headers],
            body: rows,
            startY: 20,
            styles: {
                fontSize: 7,
                cellPadding: 2,
                font: 'helvetica'
            },
            headStyles: {
                fillColor: [34, 85, 34],
                textColor: 255,
                fontStyle: 'bold',
                halign: 'center',
                minCellHeight: 8,
            },
            columnStyles: {
                0: { cellWidth: 50 },
                1: { cellWidth: 18 },
                2: { cellWidth: 22 },
                3: { cellWidth: 22 },
                4: { cellWidth: 22 },
                5: { cellWidth: 48 },
                6: { cellWidth: 38 },
                7: { cellWidth: 27 },
            },
            alternateRowStyles: {
                fillColor: [240, 248, 240]
            },
            tableWidth: 'auto',
        });

        doc.save(`declaracion_${this.tipoPlantaSelectButton}_${currentYear}.pdf`);
    }

    private normalizeTipoDatoRows(data: StockageModel[] | undefined): void {
        if (!Array.isArray(data)) {
            return;
        }

        const counter: Record<string, number> = {};

        data.forEach((row) => {
            if (!row) {
                return;
            }

            const id = String(row.id ?? '');

            if (id === '') {
                return;
            }

            const rawTipoDato = String(row.tipo_dato ?? '');

            if (!rawTipoDato.includes(';')) {
                return;
            }

            counter[id] = (counter[id] ?? 0) + 1;

            const rowIndex = counter[id] - 1;
            const parts = rawTipoDato.split(';').filter((it) => it !== '');

            row.tipo_dato = parts[rowIndex] ?? '';
        });
    }

    private buildModalModel(source: StockageModel): StockageModel {
        const rows = this.getRowsByRegisterId(source.id);
        const first = rows[0] ?? source;

        const productoLabels = this.serializeRows(rows, (row) => row.producto_nombre);
        const cantidadLabels = this.serializeRows(rows, (row) => row.unidades);
        const loteLabels = this.serializeRows(rows, (row) => row.lote);
        const marcaLabels = this.serializeRows(rows, (row) => (row as Record<string, unknown>)['marca_comercial']);
        const envaseLabels = this.serializeRows(rows, (row) => row.envase);
        const loteConselleriaLabels = this.serializeRows(rows, (row) =>
            (row as Record<string, unknown>)['lote_conselleria'] ??
            (row as Record<string, unknown>)['lote1_conselleria']
        );
        const variedadLabels = this.serializeRows(rows, (row) => this.getRowVariedadLabel(row));
        const tipoSemillaLabels = this.serializeRows(rows, (row) => row.tipo_semilla);
        const unidadesMedidaLabels = this.serializeRows(rows, (row) => row.unidades_medida);

        const tipoDato = this.firstNonEmpty(rows.map((row) => String(row.tipo_dato ?? '')));
        const proveedor = String(first.proveedor ?? '');
        const ubicacion = String(
            (first as Record<string, unknown>)['parcela'] ??
            first.ubicacion ??
            ''
        );
        const ubicacionDestino = String(
            (first as Record<string, unknown>)['parcela_destino'] ??
            first.ubicacion_destino ??
            ''
        );

        return {
            ...first,
            id: String(first.id ?? ''),
            id_cliente: String(first.id_cliente ?? ''),
            id_usuario: String(first.id_usuario ?? ''),
            id_finca: String(first.finca_nombre ?? first.finca ?? ''),
            id_comercio: proveedor,
            proveedor,
            fecha: String(first.fecha ?? ''),
            tipo: this.normalizeTipoLabel(first.tipo),
            tipo_dato: tipoDato,
            cif_proveedor: String((first as Record<string, unknown>)['cif_proveedor'] ?? ''),
            albaran: String(first.albaran ?? ''),
            ubicacion,
            ubicacion_destino: ubicacionDestino,
            fecha_injerto: String((first as Record<string, unknown>)['fecha_injerto'] ?? ''),
            articulo: String((first as Record<string, unknown>)['articulo'] ?? ''),
            observaciones: String(first.observaciones ?? ''),
            ids_productos: productoLabels,
            ids_tipos: productoLabels,
            array_unidades: cantidadLabels,
            array_lotes: loteLabels,
            array_marcas: marcaLabels,
            array_envases: envaseLabels,
            array_lotes_conselleria: loteConselleriaLabels,
            unidades_medida: unidadesMedidaLabels,
            id_variedad: variedadLabels,
            tipo_semilla: tipoSemillaLabels,
            lote1: this.firstToken(loteLabels),
            lote1_conselleria: this.firstToken(loteConselleriaLabels)
        };
    }

    private getRowsByRegisterId(id: unknown): StockageModel[] {
        const registerId = String(id ?? '');
        const currentRows = (this.getRequest.value ?? []).filter((row) => String(row.id ?? '') === registerId);

        if (currentRows.length > 0) {
            return currentRows;
        }

        const filteredRows = ((this.filter.value as StockageModel[]) ?? []).filter((row) => String(row.id ?? '') === registerId);

        return filteredRows;
    }

    private serializeRows(
        rows: StockageModel[],
        getter: (row: StockageModel) => unknown
    ): string {
        return rows.map((row) => String(getter(row) ?? '')).join(';');
    }

    private getRowVariedadLabel(row: StockageModel): string {
        const rawRow = row as Record<string, unknown>;

        return String(
            rawRow['nombre_variedad'] ??
            rawRow['id_variedad'] ??
            rawRow['variedad'] ??
            rawRow['ids_productos'] ??
            ''
        );
    }

    private normalizeTipoLabel(tipo: unknown): string {
        const value = String(tipo ?? '');

        switch (value) {
            case 'E':
            case 'Entrada':
                return 'Entrada';

            case 'S':
            case 'Salida':
                return 'Salida';

            case 'P':
            case 'Producción':
                return 'Producción';
            
            case 'MOV':
            case 'Movimiento':
                return 'Movimiento';
                
            case 'MER':
            case 'Merma':
                return 'Merma';

            default:
                return value;
        }
    }

    private firstToken(value: string): string {
        return value.split(';')[0] ?? '';
    }

    private firstNonEmpty(values: string[]): string {
        return values.find((value) => value.trim() !== '') ?? '';
    }
}